"use client";

import { useEffect, useState } from "react";

import { LocaleLink, useTranslator } from "~/i18n/provider";
import type { ActiveAnnouncement } from "~/server/announcements/service";
import { announcementDismissKey } from "./announcement-keys";

const TONE_CLASS: Record<string, string> = {
  info: "bg-secondary text-secondary-fg",
  promo: "bg-primary text-primary-fg",
  warning: "bg-warning text-warning-fg",
};

/**
 * P2-01 · P6-19 · Client wrapper for the announcement bar. Shows the highest
 * -priority active announcement; dismissing it advances to the next one (all
 * client-side). Dismissal persists per session in sessionStorage under a per-id
 * key (P2-DB03 r2 decision) so the SSR HTML stays identical for every visitor
 * (P2-15 cache-friendly). The server renders the top announcement; a pre-paint
 * inline script in `announcement-bar.tsx` hides the bar before first paint when
 * the top is already dismissed, and this component then swaps in the next.
 */
export function AnnouncementDismissible({
  announcements,
}: {
  announcements: ActiveAnnouncement[];
}) {
  const t = useTranslator();
  // Initial render matches SSR (top announcement, nothing dismissed yet); the
  // effect reads sessionStorage and corrects on the client to avoid a mismatch.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const seen = new Set<string>();
    for (const a of announcements) {
      try {
        if (sessionStorage.getItem(announcementDismissKey(a.id)) === "1")
          seen.add(a.id);
      } catch {
        // Storage unavailable (privacy mode) → nothing dismissed, bar stays.
      }
    }
    if (seen.size > 0) setDismissed(seen);
  }, [announcements]);

  const current = announcements.find((a) => !dismissed.has(a.id)) ?? null;

  // Once the client owns a visible announcement, clear any pre-paint hide the
  // inline script applied (e.g. the top was dismissed but a next one exists).
  useEffect(() => {
    if (current)
      document.getElementById("announcement-bar")?.removeAttribute("hidden");
  }, [current]);

  if (!current) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(announcementDismissKey(current.id), "1");
    } catch {
      // Best effort — still advance for the current page.
    }
    setDismissed((prev) => new Set(prev).add(current.id));
  };

  return (
    <div
      id="announcement-bar"
      role="region"
      aria-label={t("nav.announcement")}
      className={`relative px-10 py-2 text-center text-sm font-medium ${TONE_CLASS[current.tone] ?? TONE_CLASS.info}`}
    >
      {current.href ? (
        <LocaleLink
          href={current.href}
          className="underline-offset-2 hover:underline"
        >
          {current.message}
        </LocaleLink>
      ) : (
        current.message
      )}
      {current.dismissible ? (
        <button
          type="button"
          aria-label={t("nav.dismissAnnouncement")}
          onClick={dismiss}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 leading-none opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
    </div>
  );
}
